// src/context/CartContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import { getCart, saveCart } from "../Utils/cartService";
import { capQuantity, readAvailability } from "../Utils/availability";
import { useToast } from "./ToastContext";
import { ConfirmDialog } from "../ui/ConfirmDialog";

const CartContext = createContext();

const STORAGE_PREFIX = "cart:";
const SYNC_RETRIES = 3;
const SYNC_DELAY_MS = 500;

const getStorageKey = (email) => {
  if (email) return `${STORAGE_PREFIX}email:${email}`;
  return `${STORAGE_PREFIX}guest`;
};

const withRetry = async (
  operation,
  retries = SYNC_RETRIES,
  delayMs = SYNC_DELAY_MS
) => {
  for (let attempt = 0; attempt < retries; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (attempt === retries - 1) throw error;
      await new Promise((resolve) =>
        setTimeout(resolve, delayMs * 2 ** attempt)
      );
    }
  }

  return undefined;
};

const detectMergeFarmConflict = (remoteItems, guestItems) => {
  if (remoteItems.length === 0 || guestItems.length === 0) return false;
  const remoteFarm = Number(remoteItems[0].farmId) || 0;
  const guestFarm = Number(guestItems[0].farmId) || 0;
  return remoteFarm !== 0 && guestFarm !== 0 && remoteFarm !== guestFarm;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated, userEmail } = useAuth();
  const { error: toastError } = useToast();
  const { t } = useTranslation();
  const storageKey = useMemo(
    () => getStorageKey(userEmail),
    [userEmail]
  );
  const [cartItems, setCartItems] = useState([]);
  const [remoteReady, setRemoteReady] = useState(false);
  const syncingRef = useRef(false);
  const syncQueueRef = useRef({ inFlight: false, queuedItems: null });
  const [mergeConflictDialogOpen, setMergeConflictDialogOpen] = useState(false);
  const mergePendingGuestRef = useRef(null);

  const syncRemoteCart = useCallback(
    async (items) => {
      if (!isAuthenticated || !remoteReady) return;

      const queue = syncQueueRef.current;
      if (queue.inFlight) {
        queue.queuedItems = items;
        return;
      }

      queue.inFlight = true;
      let pendingItems = items;

      while (pendingItems) {
        queue.queuedItems = null;
        const payload = pendingItems.map((item) => ({
          basketId: item.id,
          quantity: item.quantity || 1,
        }));

        try {
          await withRetry(() => saveCart(payload));
        } catch (err) {
          console.error("Failed to sync cart.", err);
          toastError(
            t(
              "cart.sync_error",
              "Your cart couldn't sync. Changes are saved locally."
            )
          );
        }

        pendingItems = queue.queuedItems;
      }

      queue.inFlight = false;
    },
    [isAuthenticated, remoteReady, t, toastError]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      setCartItems([]);
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      setCartItems(Array.isArray(parsed) ? parsed : []);
    } catch {
      setCartItems([]);
    }
  }, [storageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(storageKey, JSON.stringify(cartItems));
  }, [cartItems, storageKey]);

  useEffect(() => {
    if (!isAuthenticated) {
      setRemoteReady(true);
      return;
    }
    setRemoteReady(false);
    let isActive = true;
    const loadRemote = async () => {
      try {
        const data = await getCart();
        if (!isActive) return;
        const remoteItems = (data?.items || []).map((item) => ({
          id: item.basketId,
          name: item.basketName,
          price: Number(item.price || 0),
          image: item.imageUrl || "",
          farmId: Number(item.farmId) || 0,
          farmName: item.farmName || "",
          quantity: item.quantity,
        }));
        syncingRef.current = true;
        // Read guest cart directly from localStorage so the merge isn't
        // affected by the storageKey-change effect clearing state first.
        const guestKey = `${STORAGE_PREFIX}guest`;
        let guestItems = [];
        try {
          const raw = localStorage.getItem(guestKey);
          if (raw) guestItems = JSON.parse(raw) || [];
          if (!Array.isArray(guestItems)) guestItems = [];
        } catch { guestItems = []; }

        if (detectMergeFarmConflict(remoteItems, guestItems)) {
          setCartItems(remoteItems);
          mergePendingGuestRef.current = guestItems;
          setMergeConflictDialogOpen(true);
        } else {
          setCartItems(() => {
            if (guestItems.length === 0) return remoteItems;
            if (remoteItems.length === 0) return guestItems;
            const merged = [...remoteItems];
            const remoteIds = new Set(remoteItems.map((i) => i.id));
            for (const localItem of guestItems) {
              if (!remoteIds.has(localItem.id)) {
                merged.push(localItem);
              }
            }
            return merged;
          });
        }
        // Clear guest storage after merge
        localStorage.removeItem(guestKey);
      } catch (err) {
        console.error("Failed to load cart.", err);
        toastError(
          t(
            "cart.load_error",
            "Couldn't load your saved cart. Using local copy."
          )
        );
      } finally {
        if (isActive) {
          syncingRef.current = false;
          setRemoteReady(true);
        }
      }
    };
    loadRemote();
    return () => {
      isActive = false;
    };
  }, [isAuthenticated, t, toastError]);

  useEffect(() => {
    if (!isAuthenticated || !remoteReady || syncingRef.current) return;
    const timeout = setTimeout(() => {
      syncRemoteCart(cartItems);
    }, 400);
    return () => clearTimeout(timeout);
  }, [cartItems, isAuthenticated, remoteReady, syncRemoteCart]);

  const addItemToCart = (item) => {
    const qty = item.quantity || 1;
    const incomingFarmId = Number(item.farmId) || 0;
    const existingItem = cartItems.find((i) => i.id === item.id);
    // The caller may know a fresher availability than the stored item does, so
    // the incoming value wins and null falls back to what the cart already has.
    const available = readAvailability(item) ?? readAvailability(existingItem);

    if (available === 0) {
      toastError(
        t("cart.sold_out", {
          name: item.name,
          defaultValue: "{{name}} is sold out right now.",
        })
      );
      return false;
    }

    if (existingItem) {
      const nextQuantity = capQuantity(existingItem.quantity + qty, available);
      if (nextQuantity === existingItem.quantity) {
        toastError(
          t("cart.availability_cap", {
            count: available,
            defaultValue: "Only {{count}} of this basket are available.",
          })
        );
        return false;
      }
      setCartItems(
        cartItems.map((i) =>
          i.id === item.id
            ? {
                ...i,
                farmId: Number(i.farmId) || incomingFarmId || 0,
                farmName: i.farmName || item.farmName || "",
                availableQuantity: available,
                quantity: nextQuantity,
              }
            : i
        )
      );
    } else {
      setCartItems([
        ...cartItems,
        {
          ...item,
          farmId: incomingFarmId,
          availableQuantity: available,
          quantity: capQuantity(qty, available),
        },
      ]);
    }

    return true;
  };

  const handleKeepSignedInCart = useCallback(() => {
    mergePendingGuestRef.current = null;
    setMergeConflictDialogOpen(false);
  }, []);

  const handleUseGuestCart = useCallback(() => {
    const guestItems = mergePendingGuestRef.current;
    mergePendingGuestRef.current = null;
    setMergeConflictDialogOpen(false);
    if (!guestItems || guestItems.length === 0) return;
    setCartItems(() => guestItems);
  }, []);

  const removeItemFromCart = (id) => {
    setCartItems(cartItems.filter((i) => i.id !== id));
  };

  const updateItemQuantity = (id, quantity) => {
    if (quantity < 1) return;
    setCartItems(
      cartItems.map((i) =>
        i.id === id
          ? {
              ...i,
              quantity: Math.max(1, capQuantity(quantity, readAvailability(i))),
            }
          : i
      )
    );
  };

  /**
   * Refreshes availability from a freshly fetched catalogue, keyed by basket id.
   * A cart rehydrated from localStorage can outlive the stock that filled it, so
   * quantities are pulled back down to what the farm can still build instead of
   * failing at checkout with "Insufficient stock".
   */
  const syncAvailability = useCallback((availabilityById) => {
    if (!availabilityById) return;
    setCartItems((prev) => {
      let changed = false;
      const next = prev.map((item) => {
        if (!(item.id in availabilityById)) return item;
        const available = readAvailability({
          availableQuantity: availabilityById[item.id],
        });
        const quantity = Math.max(1, capQuantity(item.quantity, available));
        if (item.availableQuantity === available && item.quantity === quantity) {
          return item;
        }
        changed = true;
        return { ...item, availableQuantity: available, quantity };
      });
      return changed ? next : prev;
    });
  }, []);

  const clearCart = () => setCartItems([]);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addItemToCart,
        removeItemFromCart,
        updateItemQuantity,
        syncAvailability,
        clearCart,
        handleUseGuestCart,
        handleKeepSignedInCart,
        mergeConflictDialogOpen,
      }}
    >
      {children}
      <ConfirmDialog
        open={mergeConflictDialogOpen}
        title={t("cart.merge_conflict_title", {
          defaultValue: "Cart conflict",
        })}
        message={t("cart.merge_conflict_message", {
          defaultValue:
            "Your saved cart has items from a different farm. Your account cart was kept by default. Would you like to replace it with your previous cart?",
        })}
        confirmLabel={t("cart.merge_conflict_use_guest", {
          defaultValue: "Use previous cart",
        })}
        cancelLabel={t("cart.merge_conflict_keep_signed_in", {
          defaultValue: "Keep current cart",
        })}
        variant="danger"
        onConfirm={handleUseGuestCart}
        onCancel={handleKeepSignedInCart}
      />
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
